/**
 * Domain hooks de MealPlan (recetario, plan semanal, lista de compra, despensa).
 *
 * Encapsulan queryKey + queryFn sobre createPowerSyncQuery para que la clave y
 * la query sean estructuralmente inseparables (guardrail de lint del template).
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type {
  Aisle,
  PantryItem,
  PowerSyncDrizzleDB,
  Recipe,
  RecipeIngredient,
  RecipeVersion,
  ShoppingItem,
  ShoppingList,
  ShoppingListOrganization,
} from "@mealplan/powersync";

import { queryKeys } from "../utils/query-keys";
import {
  getRecipeByIdQuery,
  getRecipeIngredientsQuery,
  getRecipesByIngredientNameQuery,
  getRecipesQuery,
  getRecipeVersionsQuery,
  type RecipeByIngredientRow,
} from "../recipes/queries";
import {
  getMealPlanByRecipeQuery,
  getMealPlanRangeQuery,
  type MealPlanByRecipeRow,
  type MealPlanEntryWithRecipe,
} from "../meal-plan/queries";
import {
  getAislesQuery,
  getPantryQuery,
  getShoppingItemsQuery,
  getShoppingListByIdQuery,
  getShoppingListOrganizationsQuery,
  getShoppingListsQuery,
} from "../shopping/queries";

import { createPowerSyncQuery, createPowerSyncSingle } from "./create-power-sync-query";

interface BaseOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
}

export function useRecipesQuery(
  options: BaseOptions & { search?: Accessor<string>; tag?: Accessor<string> },
) {
  return createPowerSyncQuery<Recipe>({
    queryKey: () => queryKeys.recipes.list(options.userId(), options.search?.(), options.tag?.()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid
        ? getRecipesQuery(db, uid, { search: options.search?.(), tag: options.tag?.() })
        : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function useRecipeDetailQuery(options: BaseOptions & { recipeId: Accessor<string> }) {
  return createPowerSyncSingle<Recipe>({
    queryKey: () => queryKeys.recipes.detail(options.recipeId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.recipeId();
      return db && id ? getRecipeByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(options.recipeId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
  });
}

export function useRecipeIngredientsQuery(options: BaseOptions & { recipeId: Accessor<string> }) {
  return createPowerSyncQuery<RecipeIngredient>({
    queryKey: () => queryKeys.recipes.ingredients(options.recipeId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.recipeId();
      return db && id ? getRecipeIngredientsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.recipeId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

/**
 * Historial de versiones LOCAL de una receta (L4) — bucket PowerSync acotado a
 * la ventana de sync. El historial profundo se pide bajo demanda por API.
 */
export function useRecipeVersionsQuery(options: BaseOptions & { recipeId: Accessor<string> }) {
  return createPowerSyncQuery<RecipeVersion>({
    queryKey: () => queryKeys.recipes.versions(options.recipeId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.recipeId();
      return db && id ? getRecipeVersionsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.recipeId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function useMealPlanWeekQuery(
  options: BaseOptions & { startDate: Accessor<string>; endDate: Accessor<string> },
) {
  return createPowerSyncQuery<MealPlanEntryWithRecipe>({
    queryKey: () =>
      queryKeys.mealPlan.week(options.userId(), options.startDate(), options.endDate()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid
        ? getMealPlanRangeQuery(db, uid, options.startDate(), options.endDate())
        : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

/** Entradas del plan que usan una receta (interconexión receta→plan). */
export function useMealPlanByRecipeQuery(options: BaseOptions & { recipeId: Accessor<string> }) {
  return createPowerSyncQuery<MealPlanByRecipeRow>({
    queryKey: () => queryKeys.mealPlan.byRecipe(options.userId(), options.recipeId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const id = options.recipeId();
      return db && uid && id ? getMealPlanByRecipeQuery(db, uid, id) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.drizzle()) && Boolean(options.recipeId()),
    staleTime: options.staleTime,
  });
}

/** Recetas que contienen un ingrediente por nombre (interconexión ingrediente→recetas). */
export function useRecipesByIngredientQuery(
  options: BaseOptions & { ingredientName: Accessor<string> },
) {
  return createPowerSyncQuery<RecipeByIngredientRow>({
    queryKey: () =>
      queryKeys.recipes.list(options.userId(), `ing:${options.ingredientName()}`, undefined),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const name = options.ingredientName().trim();
      return db && uid && name ? getRecipesByIngredientNameQuery(db, uid, name) : null;
    },
    enabled: () =>
      Boolean(options.userId()) &&
      Boolean(options.drizzle()) &&
      Boolean(options.ingredientName().trim()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function useShoppingListsQuery(options: BaseOptions) {
  return createPowerSyncQuery<ShoppingList>({
    queryKey: () => queryKeys.shopping.lists(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db ? getShoppingListsQuery(db) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function useShoppingListDetailQuery(options: BaseOptions & { listId: Accessor<string> }) {
  return createPowerSyncSingle<ShoppingList>({
    queryKey: () => queryKeys.shopping.detail(options.listId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.listId();
      return db && id ? getShoppingListByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(options.listId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
  });
}

export function useShoppingItemsQuery(options: BaseOptions & { listId: Accessor<string> }) {
  return createPowerSyncQuery<ShoppingItem>({
    queryKey: () => queryKeys.shopping.items(options.listId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.listId();
      return db && id ? getShoppingItemsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.listId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function useShoppingListOrgsQuery(options: BaseOptions & { listId: Accessor<string> }) {
  return createPowerSyncQuery<ShoppingListOrganization>({
    queryKey: () => queryKeys.shopping.organizations(options.listId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.listId();
      return db && id ? getShoppingListOrganizationsQuery(db, id) : null;
    },
    enabled: () => Boolean(options.listId()) && Boolean(options.drizzle()),
    staleTime: options.staleTime,
  });
}

export function useAislesQuery(options: BaseOptions) {
  return createPowerSyncQuery<Aisle>({
    queryKey: () => queryKeys.aisles.all(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getAislesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}

export function usePantryQuery(options: BaseOptions) {
  return createPowerSyncQuery<PantryItem>({
    queryKey: () => queryKeys.pantry.all(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getPantryQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
  });
}
